// packages
import { useRef, useState } from 'react'
import { toPng } from 'html-to-image'
import { saveAs } from 'file-saver'

type AsyncPoolFn<T, R> = (poolLimit: number, items: T[], iteratorFn: (item: T, index: number) => Promise<R>) => Promise<R[]>

const asyncPool: AsyncPoolFn<unknown, unknown> = async (poolLimit, items, iteratorFn) => {
  const ret: unknown[] = new Array(items.length)
  let i = 0

  const workers = new Array(poolLimit).fill(0).map(async () => {
    while (i < items.length) {
      const currentIndex = i++
      try {
        ret[currentIndex] = await iteratorFn(items[currentIndex], currentIndex)
      } catch (err) {
        console.error('Erro no asyncPool iteratorFn:', err)
        ret[currentIndex] = undefined
      }
    }
  })

  await Promise.all(workers)
  return ret
}

interface GeneratePDFOptions {
  filename?: string
  scale?: number
  concurrency?: number
  /**
   * Seletor CSS dos nós que viram páginas. Pode ser escopado (ex.
   * `#pdf-r0 .pdf-page`) para desambiguar múltiplas instâncias na mesma página.
   * Default: `.pdf-page`.
   */
  selector?: string
}

export default function useGeneratePDF() {
  const [isLoading, setIsLoading] = useState(false)
  const [progress, setProgress] = useState(0)
  const [done, setDone] = useState(0)
  const [total, setTotal] = useState(0)
  const pdfWorkerRef = useRef<Worker | null>(null)

  async function generatePDF(options?: GeneratePDFOptions) {
    const filename = options?.filename || 'documento.pdf'
    const scale = options?.scale ?? 2
    const concurrency = options?.concurrency ?? Math.max(1, Math.min(4, (navigator.hardwareConcurrency || 4) - 1))
    const selector = options?.selector || '.pdf-page'

    if (typeof window === 'undefined') return
    setIsLoading(true)
    setProgress(0)
    setDone(0)

    // Polling: aguarda os nós aparecerem no DOM. Template pode estar sendo
    // montado / re-renderizado por useEffect com RAF.
    let nodes = Array.from(document.querySelectorAll(selector)) as HTMLElement[]
    if (!nodes.length) {
      const pollStart = Date.now()
      const pollMax = 3000
      while (Date.now() - pollStart < pollMax) {
        await new Promise(r => setTimeout(r, 80))
        nodes = Array.from(document.querySelectorAll(selector)) as HTMLElement[]
        if (nodes.length > 0) break
      }
    }
    setTotal(nodes.length)

    if (!nodes.length) {
      console.warn(`Nenhuma página encontrada (${selector}) após 3s de polling. Verifique se o template está sendo renderizado off-screen.`)
      setIsLoading(false)
      return
    }

    const pdfWorker = new Worker(new URL('@/workers/pdf-save-worker.ts', import.meta.url), { type: 'module' })
    pdfWorkerRef.current = pdfWorker

    pdfWorker.onmessage = (e: MessageEvent) => {
      const msg = e.data as { type: string; pdfBuffer?: ArrayBuffer; filename?: string; message?: string }

      if (msg.type === 'finished') {
        const ab = msg.pdfBuffer!
        const blob = new Blob([ab], { type: 'application/pdf' })
        saveAs(blob, msg.filename || filename)
        setProgress(100)
        setIsLoading(false)
        try {
          pdfWorker.terminate()
        } catch {
          // ignore
        }
      } else if (msg.type === 'error') {
        console.error('PDF worker error:', msg.message)
        setIsLoading(false)
        try {
          pdfWorker.terminate()
        } catch {
          // ignore
        }
      }
    }

    pdfWorker.postMessage({ type: 'start', total: nodes.length, filename })

    const idle = () =>
      new Promise<void>(res => {
        const w = window as unknown as { requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => void }
        if (w.requestIdleCallback) w.requestIdleCallback(() => res(), { timeout: 200 })
        else setTimeout(() => res(), 0)
      })

    let completed = 0

    const iteratorFn = async (pageNode: unknown, index: number) => {
      const node = pageNode as HTMLElement
      const width = node.offsetWidth
      const height = node.offsetHeight

      const dataUrl = await toPng(node, {
        pixelRatio: scale,
        cacheBust: true,
        skipFonts: true
      })

      const res = await fetch(dataUrl)
      const ab = await res.arrayBuffer()

      const view = new Uint8Array(ab, 0, Math.min(8, ab.byteLength))
      const isPng = view.length >= 8 && view[0] === 0x89 && view[1] === 0x50 && view[2] === 0x4e && view[3] === 0x47

      if (!isPng) {
        pdfWorker.postMessage({ type: 'page-error', index, width, height, message: 'Generated buffer is not PNG' })
      } else {
        pdfWorker.postMessage({ type: 'page', index, buffer: ab, width, height }, [ab])
      }

      completed++
      setDone(completed)
      setProgress(Math.round((completed * 100) / nodes.length))
      if (completed % 2 === 0) await idle()

      return { index, width, height }
    }

    try {
      await asyncPool(concurrency, nodes as unknown[], iteratorFn)
      pdfWorker.postMessage({ type: 'end' })
    } catch (err) {
      console.error('Erro no pipeline asyncPool:', err)
      setIsLoading(false)
      try {
        pdfWorker.terminate()
      } catch {
        // ignore
      }
    }
  }

  function cancel() {
    try {
      pdfWorkerRef.current?.terminate()
    } catch {
      // ignore
    }
    setIsLoading(false)
    setProgress(0)
  }

  return { generatePDF, isLoading, progress, done, total, cancel }
}
